/* eslint-disable */
import React, { createContext, useState, ReactNode } from 'react';

export type SystemNotifyPosition = 'left' | 'center' | 'right';
export type SystemNotifyType = 'success' | 'error' | 'warning' | 'info' | '';

export interface SystemNotifyItem {
	id: string;
	message: string;
	type: SystemNotifyType;
	duration: number;
	position: SystemNotifyPosition;
}

interface SystemNotifyContextProps {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	message: string;
	setMessage: React.Dispatch<React.SetStateAction<string>>;
	type: SystemNotifyType;
	setType: React.Dispatch<React.SetStateAction<SystemNotifyType>>;
	duration?: number;
	position: SystemNotifyPosition;
	setPosition: React.Dispatch<React.SetStateAction<SystemNotifyPosition>>;
	notifications?: SystemNotifyItem[];
	removeNotify?: (id: string) => void;
	handleNotify: (
		messageProps: string,
		typeProps: SystemNotifyType,
		timeToClose?: number | SystemNotifyPosition,
		positionProps?: SystemNotifyPosition
	) => void;
}

const SystemNotifyContext = createContext<SystemNotifyContextProps>({
	isOpen: false,
	setIsOpen: () => {},
	message: '',
	setMessage: () => {},
	type: '',
	setType: () => {},
	duration: 3000,
	position: 'center',
	setPosition: () => {},
	notifications: [],
	removeNotify: () => {},
	handleNotify: () => {}
});

interface ContextProps {
	children: ReactNode;
}

export function SystemNotifyProvider({ children }: ContextProps) {
	const [isOpen, setIsOpen] = useState(false);
	const [message, setMessage] = useState('');
	const [type, setType] = useState<SystemNotifyType>('');
	const [duration, setDuration] = useState(3000);
	const [position, setPosition] = useState<SystemNotifyPosition>('center');
	const [notifications, setNotifications] = useState<SystemNotifyItem[]>([]);

	function handleNotify(
		messageProps: string,
		typeProps: SystemNotifyType,
		timeToClose: number | SystemNotifyPosition = 3000,
		positionProps: SystemNotifyPosition = 'center'
	) {
		const notifyDuration = typeof timeToClose === 'number' ? timeToClose : 3000;
		const notifyPosition =
			typeof timeToClose === 'string' ? timeToClose : positionProps;

		setIsOpen(true);
		setMessage(messageProps);
		setType(typeProps);
		setDuration(notifyDuration);
		setPosition(notifyPosition);
		setNotifications((list) => [
			...list,
			{
				id: `notify-${Date.now()}-${Math.random().toString(36).slice(2)}`,
				message: messageProps,
				type: typeProps,
				duration: notifyDuration,
				position: notifyPosition
			}
		]);
	}

	function removeNotify(id: string) {
		setNotifications((list) => {
			const nextList = list.filter((item) => item.id !== id);

			if (nextList.length === 0) {
				setIsOpen(false);
				setMessage('');
				setType('');
			}

			return nextList;
		});
	}

	return (
		<SystemNotifyContext.Provider
			value={{
				isOpen,
				setIsOpen,
				message,
				setMessage,
				setType,
				type,
				duration,
				position,
				setPosition,
				notifications,
				removeNotify,
				handleNotify
			}}
		>
			{children}
		</SystemNotifyContext.Provider>
	);
}

export default SystemNotifyContext;
